import { useRef, useState } from "react";
import { Box, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/Upload";
import { alpha } from "@mui/material/styles";
import { useUploadImage } from "@/api/media";
import { useProjectDetails, useSetProjectThumbnail } from "@/api/project";
import { computeProjectPermissions } from "@/utils/projectPermissions";
import { useCurrentUserEmail } from "@/hooks/useProjectPermissions";
import { toastError, toastSuccess } from "@/utils/toast";

const SET_THUMBNAIL_DIALOG_TITLE_ID = "set-thumbnail-dialog-title";

export interface SetThumbnailDialogProps {
  readonly open: boolean;
  readonly projectId: string | null;
  readonly onClose: () => void;
}

const SetThumbnailDialog = ({ open, projectId, onClose }: SetThumbnailDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const uploadImageMutation = useUploadImage();
  const setThumbnailMutation = useSetProjectThumbnail();
  const currentUserEmail = useCurrentUserEmail();
  const projectDetailsQuery = useProjectDetails(open ? projectId : null);
  const { canWrite } = computeProjectPermissions(
    currentUserEmail,
    projectDetailsQuery.data?.user_name,
    projectDetailsQuery.data?.is_locked,
    projectDetailsQuery.data?.locked_by
  );

  const isBusy = isUploading || setThumbnailMutation.isPending;

  const handleFileSelected = async (file: File) => {
    if (!projectId) {
      return;
    }

    if (!canWrite) {
      toastError("This project is locked by its owner. Only the owner can make changes while it is locked.", { position: "bottom-left" });
      return;
    }

    setIsUploading(true);
    try {
      const { imageId } = await uploadImageMutation.mutateAsync({ projectId, file, source: "upload" });
      await setThumbnailMutation.mutateAsync({ projectId, imageId });
      toastSuccess("Project thumbnail updated");
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to set project thumbnail";
      toastError(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={SET_THUMBNAIL_DIALOG_TITLE_ID}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            width: 480,
            maxWidth: "calc(100vw - 48px)",
            borderRadius: "12px"
          }
        }
      }}
    >
      <Box
        sx={{
          height: 56,
          px: 3,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottomWidth: 1,
          borderBottomStyle: "solid",
          borderBottomColor: "divider"
        }}
      >
        <Typography id={SET_THUMBNAIL_DIALOG_TITLE_ID} sx={{ fontSize: 16, fontWeight: 500, letterSpacing: "0.15px" }}>
          Set thumbnail
        </Typography>
        <IconButton onClick={onClose} sx={{ p: 1, borderRadius: 999 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ px: 3, pt: 3, pb: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) {
              void handleFileSelected(file);
            }
          }}
        />
        <Box
          role="button"
          tabIndex={0}
          onClick={() => !isBusy && canWrite && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !isBusy && canWrite) {
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!isBusy && canWrite) {
              setIsDraggingOver(true);
            }
          }}
          onDragLeave={() => setIsDraggingOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file && !isBusy && canWrite) {
              void handleFileSelected(file);
            }
          }}
          sx={(theme) => ({
            width: 80,
            height: 80,
            minWidth: 80,
            minHeight: 80,
            flexShrink: 0,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isDraggingOver ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.common.white, 0.06),
            border: "1px dashed",
            borderColor: isDraggingOver ? "primary.main" : "divider",
            cursor: isBusy || !canWrite ? "default" : "pointer",
            opacity: isBusy || !canWrite ? 0.6 : 1,
            transition: "background-color 0.15s, border-color 0.15s",
            "&:hover": isBusy || !canWrite ? undefined : { bgcolor: alpha(theme.palette.common.white, 0.1) }
          })}
        >
          {isUploading ? <CircularProgress size={24} /> : <UploadIcon sx={{ fontSize: 28, color: isDraggingOver ? "primary.main" : "text.secondary" }} />}
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
          {canWrite ? "Upload a new image or drag and drop it here." : "This project is locked. Only the owner can change the thumbnail."}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default SetThumbnailDialog;
